/**
 * React hook that wraps the widget action governance pipeline:
 *
 *   1. Optional confirm dialog (when `action.confirm` is declared) — routed
 *      through the shared {@link ConfirmActionDialog} (NOT `window.confirm`),
 *      so the prompt is accessible (focus trap, aria) and matches every other
 *      destructive-action confirmation in the product.
 *   2. Idempotency-Key header — fresh UUID per click — so the gateway
 *      can dedupe accidental double-clicks at the audit layer.
 *   3. POST to the gateway's invoke endpoint via `useApiClient` so the
 *      auth + tenant headers are attached.
 *   4. Optimistic-rollback callback when the action declares
 *      `optimistic.rollbackOnError`.
 *
 * The hook composes `runWidgetActionInvocation` from
 * `./widget-action-flow.ts`, which carries the pure flow used by both
 * production and the unit tests.
 *
 * Because the confirmation is now a real dialog component, the hook returns a
 * `confirmDialog` node the consumer MUST render (e.g. `{action.confirmDialog}`)
 * for confirm-gated actions to prompt. Actions without `action.confirm` never
 * render anything, so nothing changes for them.
 */

import * as React from 'react';
import { useApiClient } from '@tyndo/hooks';
import type { WidgetAction, WidgetDescriptor } from '@tyndo/types';
import { ConfirmActionDialog } from '../domain/confirm-action-dialog';
import {
  runWidgetActionInvocation,
  safeUuid,
  type WidgetActionInvokeResult,
} from './widget-action-flow';

// Re-export the pure-logic public surface so consumers can import the
// React hook AND the flow primitives from `@tyndo/ui-kit`.
export {
  runWidgetActionInvocation,
  type WidgetActionInvocationDeps,
  type WidgetActionInvocationInput,
  type WidgetActionInvokeResult,
} from './widget-action-flow';

// ────────────────────────────────────────────────────────────────────────
// Public shape returned by the hook.
// ────────────────────────────────────────────────────────────────────────

export interface UseWidgetActionState {
  isPending: boolean;
  error: Error | null;
  lastResult: WidgetActionInvokeResult | null;
}

export interface UseWidgetActionApi extends UseWidgetActionState {
  invoke: (params?: unknown) => Promise<WidgetActionInvokeResult | null>;
  /**
   * Confirmation dialog node for confirm-gated actions. Render it in the
   * component tree (it renders nothing until a confirm-gated `invoke()` is in
   * flight). Actions without `action.confirm` never surface a dialog.
   */
  confirmDialog: React.ReactNode;
}

export interface UseWidgetActionOptions {
  onSuccess?: (result: WidgetActionInvokeResult) => void;
  onError?: (error: Error) => void;
  /**
   * Optimistic rollback. Called when the POST fails and the action
   * declares `optimistic.rollbackOnError`. The hook itself owns no
   * mutable view-state, so this callback is where the consuming
   * component reverses the optimistic update.
   */
  onRollback?: () => void;
}

/** Internal state for the in-flight confirmation dialog. */
interface PendingConfirm {
  title: string;
  body: string;
  confirmVerb: string;
  resolve: (ok: boolean) => void;
}

/**
 * useWidgetAction — fire `action.actionId` on `instanceId` with governance.
 *
 * `descriptor` is the widget's full descriptor (carried only for context
 * + future telemetry hooks); `action` is the specific action being
 * invoked. The hook returns `{ invoke, isPending, error, lastResult,
 * confirmDialog }`.
 */
export function useWidgetAction(
  instanceId: string,
  // descriptor is intentionally part of the signature even though the
  // hook does not currently read it — keeps the call-site stable when
  // widget-level telemetry lands. (A middle parameter, so no-unused-vars
  // doesn't flag it and no disable directive is needed.)
  descriptor: WidgetDescriptor,
  action: WidgetAction,
  options: UseWidgetActionOptions = {},
): UseWidgetActionApi {
  const apiClient = useApiClient();
  const [state, setState] = React.useState<UseWidgetActionState>({
    isPending: false,
    error: null,
    lastResult: null,
  });
  const [pendingConfirm, setPendingConfirm] = React.useState<PendingConfirm | null>(null);

  const invoke = React.useCallback(
    async (params?: unknown): Promise<WidgetActionInvokeResult | null> => {
      setState((s) => ({ ...s, isPending: true, error: null }));
      try {
        const result = await runWidgetActionInvocation(
          { instanceId, action, parameters: params },
          {
            generateUuid: () => safeUuid(),
            // Route the confirm through the shared dialog: open it and await
            // the operator's decision instead of the blocking `window.confirm`.
            confirm: () =>
              new Promise<boolean>((resolve) => {
                setPendingConfirm({
                  title: action.confirm?.title ?? 'Confirm action',
                  body: action.confirm?.body ?? '',
                  confirmVerb: action.label ?? 'Confirm',
                  resolve: (ok) => {
                    setPendingConfirm(null);
                    resolve(ok);
                  },
                });
              }),
            post: async (url, body, headers) => {
              return await apiClient.post<WidgetActionInvokeResult, unknown>(url, body, {
                headers,
              });
            },
            onSuccess: options.onSuccess,
            onError: options.onError,
            onRollback: options.onRollback,
          },
        );
        setState({ isPending: false, error: null, lastResult: result });
        return result;
      } catch (rawError) {
        const error = rawError instanceof Error ? rawError : new Error(String(rawError));
        setState((s) => ({ ...s, isPending: false, error }));
        return null;
      }
    },
    [apiClient, instanceId, action, options.onSuccess, options.onError, options.onRollback],
  );

  const confirmDialog = pendingConfirm ? (
    <ConfirmActionDialog
      open
      title={pendingConfirm.title}
      description={pendingConfirm.body || undefined}
      confirmVerb={pendingConfirm.confirmVerb}
      onConfirm={() => pendingConfirm.resolve(true)}
      onCancel={() => pendingConfirm.resolve(false)}
    />
  ) : null;

  return { ...state, invoke, confirmDialog };
}
